import type { CapturedStyle } from 'modules/manager/domain/style-clipboard';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface StyleClipboardContextValue {
  captured: CapturedStyle | null;
  setCaptured: (captured: CapturedStyle) => void;
  clear: () => void;
}

const StyleClipboardContext = createContext<StyleClipboardContextValue | null>(null);

interface StyleClipboardProviderProps {
  children: React.ReactNode;
}

/**
 * App-level style clipboard for the media-manager editor. Lives for the page
 * session (not Redux — it is not editor state and not a server resource).
 */
export const StyleClipboardProvider: React.FC<StyleClipboardProviderProps> = ({ children }) => {
  const [captured, setCapturedState] = useState<CapturedStyle | null>(null);

  const setCaptured = useCallback((next: CapturedStyle) => setCapturedState(next), []);
  const clear = useCallback(() => setCapturedState(null), []);

  const value = useMemo(() => ({ captured, setCaptured, clear }), [captured, setCaptured, clear]);

  return <StyleClipboardContext.Provider value={value}>{children}</StyleClipboardContext.Provider>;
};

export function useStyleClipboard(): StyleClipboardContextValue {
  const ctx = useContext(StyleClipboardContext);
  if (!ctx) throw new Error('useStyleClipboard must be used within a StyleClipboardProvider');
  return ctx;
}
